import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Mail, Phone, MapPin, Linkedin, Instagram, Facebook, ChevronDown } from "lucide-react";
import { org } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);
  const faq = t("contact.faq", { returnObjects: true });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.toastSuccess"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t("contact.toastError"));
    }
    setLoading(false);
  };

  return (
    <div className="relative">
      <section className="pt-16 pb-12">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-5">{t("contact.eyebrow")}</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95] max-w-3xl text-balance">
            {t("contact.title")}
          </h1>
          <p className="mt-8 font-editorial text-2xl text-iyaf-ink/75 leading-snug max-w-2xl">
            {t("contact.lead")}
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-iyaf grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white border border-black/5 p-8" data-testid="contact-info">
              <p className="eyebrow text-iyaf-blue mb-5">{t("contact.reachTeam")}</p>
              <ul className="space-y-5">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-iyaf-blue/10 flex items-center justify-center text-iyaf-blue shrink-0"><Mail size={15} /></span>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-iyaf-ink/50">{t("common.email")}</p>
                    <a className="font-display text-lg font-semibold hover:text-iyaf-blue" href={`mailto:${org.email}`}>{org.email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-iyaf-blue/10 flex items-center justify-center text-iyaf-blue shrink-0"><Phone size={15} /></span>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-iyaf-ink/50">{t("common.phone")}</p>
                    <p className="font-display text-lg font-semibold">{org.phone}</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-iyaf-blue/10 flex items-center justify-center text-iyaf-blue shrink-0"><MapPin size={15} /></span>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-iyaf-ink/50">{t("contact.labelVisit")}</p>
                    <p className="font-display text-lg font-semibold leading-snug">{org.hq}</p>
                  </div>
                </li>
              </ul>
              <div className="mt-8 pt-6 border-t border-black/5 flex gap-2">
                <a href={org.socials.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-iyaf-blue hover:text-white hover:border-iyaf-blue transition"><Linkedin size={16} /></a>
                <a href={org.socials.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-iyaf-blue hover:text-white hover:border-iyaf-blue transition"><Instagram size={16} /></a>
                <a href={org.socials.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-iyaf-blue hover:text-white hover:border-iyaf-blue transition"><Facebook size={16} /></a>
              </div>
            </div>

            <div className="mt-6 rounded-3xl overflow-hidden border border-black/5" data-testid="contact-map">
              <iframe
                title="IYAF location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=35.890%2C31.945%2C35.940%2C31.975&layer=mapnik&marker=31.96%2C35.915"
                className="w-full h-72 border-0"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-3xl bg-white border border-black/5 p-8" data-testid="contact-form">
              <p className="eyebrow text-iyaf-blue mb-5">{t("contact.formEyebrow")}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-iyaf-ink/55 font-semibold">{t("contact.fieldNameRequired")}</span>
                  <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full p-3 rounded-xl border border-black/10 focus:border-iyaf-blue outline-none" />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-iyaf-ink/55 font-semibold">{t("contact.fieldEmailRequired")}</span>
                  <input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full p-3 rounded-xl border border-black/10 focus:border-iyaf-blue outline-none" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs uppercase tracking-widest text-iyaf-ink/55 font-semibold">{t("contact.fieldSubject")}</span>
                  <input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 w-full p-3 rounded-xl border border-black/10 focus:border-iyaf-blue outline-none" />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-xs uppercase tracking-widest text-iyaf-ink/55 font-semibold">{t("contact.fieldMessageRequired")}</span>
                  <textarea required rows={6} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 w-full p-3 rounded-xl border border-black/10 focus:border-iyaf-blue outline-none resize-none font-sans" />
                </label>
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-iyaf-ink text-white font-semibold hover:bg-iyaf-blue transition disabled:opacity-50">
                {loading ? t("contact.sending") : t("contact.sendButton")}
              </button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-4">{t("contact.faqEyebrow")}</p>
          <h2 className="h2 mb-10">{t("contact.faqTitle")}</h2>
          <div className="rounded-3xl border border-black/5 bg-white divide-y divide-black/5">
            {Array.isArray(faq) && faq.map((f, i) => (
              <button
                key={i}
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                data-testid={`faq-${i}`}
                className="w-full text-left p-6 sm:p-7 flex items-start justify-between gap-6"
              >
                <div>
                  <p className="font-display text-lg font-semibold">{f.q}</p>
                  {openIdx === i && <p className="mt-3 text-iyaf-ink/70 leading-relaxed">{f.a}</p>}
                </div>
                <ChevronDown size={18} className={`shrink-0 transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
